/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import {Helmet} from 'react-helmet'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {
    Box,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    Text,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useShopperCustomersMutation, useCustomerId} from '@salesforce/commerce-sdk-react'
import {useWishList} from '../../hooks/use-wish-list'
import {useProductViewModal} from '../../hooks/use-product-view-modal'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import {useGtm} from '../../integrations/gtm/app/hooks/use-gtm'
import ProductView from '../product-view'

import {
    TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST,
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST
} from '../../constants'

/**
 * A Modal that contains Product View
 */
const QuickViewModal = ({product, isOpen, onClose, ...props}) => {
    const {formatMessage} = useIntl()
    const quickViewModalData = useProductViewModal(product)
    const isMobile = useBreakpointValue({base: true, sm: false, md: false})
    const isTablet = useBreakpointValue({base: false, sm: true, md: false})
    const toast = useToast()
    const customerId = useCustomerId()
    const segment = useSegment()
    const gtm = useGtm()

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const [isItemAddedtToWishlist, setIsItemAddedtToWishlist] = useState(false)
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const handleAddToWishlist = (product, variant, quantity) => {
        const wishListId = wishlist?.productWishList?.find((item) => item?.id)?.id
        createCustomerProductListItem.mutate(
            {
                parameters: {
                    listId: wishListId,
                    customerId
                },
                body: {
                    // NOTE: API does not respect quantity, it always adds 1
                    quantity,
                    productId: variant?.productId || product?.id,
                    public: false,
                    priority: 1,
                    type: 'product'
                }
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST),
                        status: 'success'
                    })
                    // gtm
                    gtm.sendProductToWishList('addToWishlist', product)
                    // segment
                    segment.sendProductToWishList(product, wishlist)
                    setIsItemAddedtToWishlist(true)
                }
            }
        )
    }
    const handleRemoveFromWishlist = (product) => {
        const listId = wishlist?.productWishList?.find((item) => item?.id)?.id
        const itemId = wishlist?.productWishList
            ?.flatMap((list) => list?.customerProductListItems || [])
            .find((item) => item?.productId === product?.productId)?.id

        deleteCustomerProductListItem.mutate(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        isClosable: false,
                        duration: 3000
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
                        status: 'error',
                        isClosable: false,
                        duration: 3000
                    })
                    // segment
                    segment.sendRemoveProductFromWishlist(product, wishlist)
                    setIsItemAddedtToWishlist(false)
                }
            }
        )
    }
    return (
        <Modal size="lg" maxsize="4xl" isOpen={isOpen} onClose={onClose}>
            <Helmet>
                <link
                    rel="stylesheet"
                    type="text/css"
                    href="https://cdn.jsdelivr.net/npm/custom-react-inner-image-zoom@3.0.6/lib/InnerImageZoom/styles.css"
                />
            </Helmet>
            <ModalOverlay />
            <ModalContent
                data-testid="product-view-modal quick-view-dialog"
                borderRadius={'lg'}
                px={3}
                maxWidth={'65rem'}
                width={{base: '95%', md: '90%', lg: '100%'}}
                overflowY={{base: 'hidden', md: 'unset'}}
                height={isMobile || isTablet ? '100%' : ''}
                margin={isMobile || isTablet ? 'unset' : '1.75rem auto'}
            >
                <Box className={'modal-content'}>
                    <ModalHeader
                        justifyContent="space-between"
                        borderBottom={'1px solid black'}
                        padding={4}
                    >
                        <Text
                            as={Link}
                            to={`/product/${quickViewModalData?.product?.id}`}
                            style={{fontSize: '16px'}}
                        >
                            <FormattedMessage
                                defaultMessage="Select Size"
                                id="product_view.button.select_size"
                            />
                        </Text>
                        <ModalCloseButton size="lg" color="black" fontSize="xl" />
                    </ModalHeader>
                    <ModalBody
                        bg="white"
                        padding={isMobile ? '10px' : '0'}
                        my={4}
                        overflowY={isMobile ? 'scroll' : 'unset'}
                        overflowX={'hidden'}
                        height={isMobile || isTablet ? '90vh' : 'unset'}
                    >
                        <ProductView
                            showFullLink={true}
                            imageSize="sm"
                            product={quickViewModalData.product}
                            isLoading={quickViewModalData.isFetching}
                            isQuickview={true}
                            addToWishlist={handleAddToWishlist}
                            isWishlistLoading={isWishlistLoading}
                            removeFromWishlist={handleRemoveFromWishlist}
                            isItemAddedtToWishlist={isItemAddedtToWishlist}
                            onClose={onClose}
                            {...props}
                            flexDirection="column"
                        />
                    </ModalBody>
                </Box>
            </ModalContent>
        </Modal>
    )
}

QuickViewModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    product: PropTypes.object,
    isLoading: PropTypes.bool,
    actionButtons: PropTypes.node,
    onModalClose: PropTypes.func
}

export default QuickViewModal
